import { useState } from 'react';
import { SearchInput, useInputHelper, SearchInputProps } from 'framework/form';
import { useProjectAccountOwnerAlias } from './hooks/useRisks';
import { useProjectId } from 'domain/shared/project';
import { useEmployeeService } from 'domain/shared/employee';
import { useProjectTeamService, PROJECT_READ_TEAM_MEMBER_ALIAS } from 'domain/shared/newProject';
import { useAsyncValue } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';

export const EXTERNAL_USER_PREFIX = 'external:';

const buildEmployeeAutoSuggestLabel = (employee: any) => {
  return !!employee && `${employee.nameEn.firstName} ${employee.nameEn.lastName}`;
};

interface RiskOwnerDropdownProps extends Omit<SearchInputProps, 'searchRequest'> {
  source: string;
  withContractors?: boolean;
  label?: string;
  disabled?: boolean;
}

const RiskOwnerDropdown = ({ source, mapResults, withContractors = false, ...props }: RiskOwnerDropdownProps) => {
  const { searchWithContractors, advancedSearch, getEmployeeName } = useEmployeeService();

  const { getValue, getRawOnChangeCallback } = useInputHelper({ source, ...props });
  const onChangeCallback = getRawOnChangeCallback();

  const [rawSearchValue, setRawSearchValue] = useState<string>('');

  const id = useProjectId();

  const { getProjectTeamAliases } = useProjectTeamService();
  const { isGranted } = useAuthorization();
  const needsSuggestions = !props.disabled;
  const canReadTeamAliases = needsSuggestions && isGranted(PROJECT_READ_TEAM_MEMBER_ALIAS);

  const accountOwnerAlias = useProjectAccountOwnerAlias(needsSuggestions ? id : undefined);
  const teamAliases = useAsyncValue(
    () => (canReadTeamAliases ? getProjectTeamAliases(id!) : Promise.resolve([])),
    [id, canReadTeamAliases],
  );

  const aliases = [
    ...(teamAliases?.map(member => member.employeeAlias) ?? []),
    ...(accountOwnerAlias ? [accountOwnerAlias] : []),
  ];

  const filters = [
    {
      filterType: 'FILTER_GROUP',
      condition: 'OR',
      filters: [
        {
          filterType: 'FILTER',
          property: 'specification:isStaffingCoordinator',
          operator: 'EQ',
          value: true,
        },
        {
          filterType: 'FILTER',
          property: 'specification:isCLevelManager',
          operator: 'EQ',
          value: true,
        },
        {
          filterType: 'FILTER',
          property: 'alias',
          operator: 'IN',
          value: aliases,
        },
      ],
    },
    {
      filterType: 'FILTER',
      property: 'workingStatus',
      operator: 'EQ',
      value: 'ACTIVE',
    },
    {
      filterType: 'FILTER',
      property: 'searchString',
      operator: 'CT',
      value: rawSearchValue,
    },
  ];

  const searchMethod = withContractors
    ? searchWithContractors
    : () =>
        advancedSearch({
          filter: {
            filterType: 'FILTER_GROUP',
            condition: 'AND',
            filters: filters,
          },
        });

  const mapResponse = (employee: any) => ({
    id: employee.alias,
    text: buildEmployeeAutoSuggestLabel(employee),
  });

  const getInitValueName = async () => {
    const value = getValue();
    if (!value) {
      return null;
    } else if (value?.startsWith(EXTERNAL_USER_PREFIX)) {
      return value.slice(EXTERNAL_USER_PREFIX.length);
    } else {
      const resultName = await getEmployeeName(value);
      return resultName ? `${resultName.firstName} ${resultName.lastName}` : 'unknown internal user';
    }
  };

  const onInputChangeCallback = (resultValue: string) => {
    setRawSearchValue(resultValue);

    onChangeCallback?.({ [source]: `${EXTERNAL_USER_PREFIX}${resultValue}` });
  };

  return (
    <SearchInput
      {...props}
      source={source}
      noResultMessage="No Employees are found."
      mapResults={mapResults || mapResponse}
      customGetInitialValue={getInitValueName}
      searchRequest={searchMethod}
      onInputChangeCallback={onInputChangeCallback}
    />
  );
};

export default RiskOwnerDropdown;
